import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { Inbox, CheckCircle2, AlertCircle, Shield, CircleDollarSign } from 'lucide-react';
import { AppShell } from '@/components/app/AppShell';
import {
  TicketKind, TicketStatus, useAdminTickets, useDecideVerification, usePendingVerifications,
  useTicketStats, useUpdateTicketStatus,
} from '@/lib/admin';

const KIND_LABEL: Record<TicketKind, string> = {
  verification: 'admin.kind.verification',
  support:      'admin.kind.support',
  abuse:        'admin.kind.abuse',
  billing:      'admin.kind.billing',
};
const KIND_ICON: Record<TicketKind, React.ComponentType<{ className?: string }>> = {
  verification: Shield, support: Inbox, abuse: AlertCircle, billing: CircleDollarSign,
};
const STATUS_COLOR: Record<TicketStatus, string> = {
  open:         'bg-sky-500/10 text-sky-700 dark:text-sky-300',
  in_progress:  'bg-indigo-500/10 text-indigo-700 dark:text-indigo-300',
  waiting:      'bg-amber-500/10 text-amber-700 dark:text-amber-300',
  resolved:     'bg-emerald-500/10 text-emerald-700 dark:text-emerald-300',
  closed:       'bg-slate-500/10 text-slate-700 dark:text-slate-300',
};

export function AdminInboxPage() {
  const { t } = useTranslation();
  const [filter, setFilter] = useState<{ status?: TicketStatus; kind?: TicketKind }>({ status: 'open' });
  const stats = useTicketStats();
  const tickets = useAdminTickets(filter);
  const updateStatus = useUpdateTicketStatus();

  return (
    <AppShell>
      <div className="px-6 md:px-10 py-8 max-w-7xl mx-auto space-y-6">
        <header>
          <p className="text-xs uppercase tracking-[0.2em] text-primary font-semibold">{t('admin.eyebrow')}</p>
          <h1 className="display serif-jp text-3xl md:text-4xl font-semibold tracking-tight">{t('admin.inboxTitle')}</h1>
          <p className="mt-1 text-sm text-muted-foreground">{t('admin.inboxSubtitle')}</p>
        </header>

        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          {(['open', 'in_progress', 'waiting', 'resolved', 'closed'] as TicketStatus[]).map((s) => (
            <button
              key={s}
              onClick={() => setFilter((f) => ({ ...f, status: s }))}
              className={`relative overflow-hidden rounded-2xl border p-4 text-start transition ${filter.status === s ? 'border-primary/60 shadow-glow bg-primary/5' : 'border-border bg-card hover:border-primary/40'}`}
            >
              <p className="text-[10px] uppercase tracking-wider font-semibold text-muted-foreground">{t(`admin.status.${s}`)}</p>
              <p className="num mt-1 text-2xl font-bold">{stats.data?.[s] ?? 0}</p>
            </button>
          ))}
        </div>

        <div className="flex flex-wrap gap-2">
          <KindChip active={!filter.kind} onClick={() => setFilter((f) => ({ ...f, kind: undefined }))} label={t('admin.kind.all')} />
          {(Object.keys(KIND_LABEL) as TicketKind[]).map((k) => (
            <KindChip
              key={k}
              active={filter.kind === k}
              onClick={() => setFilter((f) => ({ ...f, kind: k }))}
              label={t(KIND_LABEL[k])}
            />
          ))}
        </div>

        <div className="rounded-2xl border border-border bg-card shadow-soft overflow-hidden">
          {tickets.isLoading ? (
            <p className="px-5 py-8 text-sm text-muted-foreground">{t('common.loading')}</p>
          ) : tickets.isError ? (
            <div className="px-5 py-12 text-center">
              <p className="text-sm font-medium text-rose-600">
                {t('admin.loadError', { defaultValue: "Couldn't load tickets. Try again." })}
              </p>
              <button
                onClick={() => tickets.refetch()}
                className="mt-3 inline-flex h-9 items-center px-4 rounded-lg bg-primary/10 text-primary text-sm font-semibold hover:bg-primary/20"
              >
                {t('common.retry', { defaultValue: 'Retry' })}
              </button>
            </div>
          ) : (tickets.data ?? []).length === 0 ? (
            <p className="px-5 py-12 text-center text-sm text-muted-foreground">{t('admin.empty')}</p>
          ) : (
            <ul className="divide-y divide-border">
              {(tickets.data ?? []).map((tk) => {
                const Icon = KIND_ICON[tk.kind];
                return (
                  <motion.li
                    key={tk.id}
                    initial={{ opacity: 0, y: 6 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="px-5 py-3 flex items-start gap-3 hover:bg-muted/20 transition"
                  >
                    <div className="h-9 w-9 grid place-items-center rounded-xl bg-gradient-to-br from-indigo-500 to-fuchsia-500 text-white">
                      <Icon className="h-4 w-4" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-baseline justify-between gap-3">
                        <p className="text-sm font-semibold truncate">{tk.subject}</p>
                        <span className={`text-[10px] uppercase tracking-wider font-semibold px-2 py-1 rounded-md shrink-0 ${STATUS_COLOR[tk.status]}`}>
                          {t(`admin.status.${tk.status}`)}
                        </span>
                      </div>
                      {tk.body && <p className="text-xs text-muted-foreground mt-0.5 line-clamp-2">{tk.body}</p>}
                      <div className="mt-2 flex gap-2">
                        {tk.status !== 'resolved' && tk.status !== 'closed' && (
                          <button
                            onClick={() => updateStatus.mutate({ id: tk.id, status: 'resolved' })}
                            className="inline-flex items-center gap-1 text-xs font-semibold text-emerald-600 hover:underline"
                          >
                            <CheckCircle2 className="h-3.5 w-3.5" />
                            {t('admin.resolve')}
                          </button>
                        )}
                      </div>
                    </div>
                  </motion.li>
                );
              })}
            </ul>
          )}
        </div>

        <PendingVerifications />
      </div>
    </AppShell>
  );
}

function PendingVerifications() {
  const { t } = useTranslation();
  const pending = usePendingVerifications();
  const decide = useDecideVerification();
  if (!pending.data || pending.data.length === 0) return null;
  return (
    <section className="rounded-2xl border border-border bg-card shadow-soft">
      <div className="px-5 py-3 border-b border-border bg-muted/30">
        <h2 className="text-sm font-semibold">{t('admin.pendingVerifications')}</h2>
      </div>
      <ul className="divide-y divide-border">
        {pending.data.map((c) => (
          <li key={c.id} className="px-5 py-3 flex items-center gap-3">
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold truncate">{c.name}</p>
              <p className="text-xs text-muted-foreground">{c.verificationStatus} · {new Date(c.createdAt).toLocaleDateString()}</p>
            </div>
            <button
              onClick={() => decide.mutate({ companyId: c.id, status: 'verified' })}
              className="text-xs font-semibold text-emerald-600 hover:underline"
            >
              {t('admin.approve')}
            </button>
            <button
              onClick={() => decide.mutate({ companyId: c.id, status: 'rejected' })}
              className="text-xs font-semibold text-rose-600 hover:underline"
            >
              {t('admin.reject')}
            </button>
          </li>
        ))}
      </ul>
    </section>
  );
}

function KindChip({ active, onClick, label }: { active: boolean; onClick: () => void; label: string }) {
  return (
    <button
      onClick={onClick}
      className={`px-3 py-1.5 rounded-full text-xs font-semibold transition ${active ? 'bg-primary text-primary-foreground shadow-glow' : 'border border-border text-foreground/70 hover:bg-muted'}`}
    >
      {label}
    </button>
  );
}
